import { useEffect, useState } from 'react';
import { GeoJSON, Polyline, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// Map-side interactions for the vineyard split tool. Mounted only while a
// split session is active; all state lives in App (splitSession).
//
// Interaction contract mirrors the draw tool (DrawLayer):
// - cut stage freezes drag-panning; scroll zoom stays live; crosshair cursor
// - click places a point, double-click finishes the current cut line
// - right-click / Esc undo the last point (then re-open the last line)
// - existing polygons ignore the pointer for the whole session
//   (.map--draw-session), so cuts can cross other vineyards freely

const CLICK_SLOP_PX = 5;
const CUT_COLOR = '#f97316';
const PART_COLORS = ['#60a5fa', '#f472b6', '#4ade80', '#facc15', '#a78bfa', '#fb923c'];

export default function SplitLayer({ session, onAddPoint, onUndoPoint, onFinishLine }) {
  const map = useMap();
  const [downAt, setDownAt] = useState(null);
  const cutting = session.stage === 'cut';

  useEffect(() => {
    const c = map.getContainer();
    c.classList.add('map--draw-session');
    return () => c.classList.remove('map--draw-session');
  }, [map]);

  useEffect(() => {
    const c = map.getContainer();
    if (cutting) {
      map.dragging.disable();
      map.doubleClickZoom.disable();
      c.classList.add('map--drawing');
    } else {
      map.dragging.enable();
      map.doubleClickZoom.disable(); // dblclick stays "finish line" all session
      c.classList.remove('map--drawing');
    }
    return () => {
      map.dragging.enable();
      map.doubleClickZoom.enable();
      c.classList.remove('map--drawing');
    };
  }, [map, cutting]);

  useEffect(() => {
    if (!cutting) return;
    const onKey = (e) => { if (e.key === 'Escape') onUndoPoint(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cutting, onUndoPoint]);

  useMapEvents({
    mousedown: (e) => setDownAt(e.containerPoint),
    click: (e) => {
      if (!cutting) return;
      // moved more than the slop: a (frozen) pan attempt, not a point
      if (downAt && e.containerPoint.distanceTo(downAt) > CLICK_SLOP_PX) return;
      const open = session.open;
      // ignore the duplicate point of a double-click's second click
      if (open.length > 0) {
        const lp = map.latLngToContainerPoint(L.latLng(open[open.length - 1][0], open[open.length - 1][1]));
        if (e.containerPoint.distanceTo(lp) <= 2) return;
      }
      onAddPoint([e.latlng.lat, e.latlng.lng]);
    },
    dblclick: (e) => {
      L.DomEvent.stop(e.originalEvent);
      if (cutting) onFinishLine();
    },
    contextmenu: (e) => {
      e.originalEvent.preventDefault();
      if (cutting) onUndoPoint();
    },
  });

  return (
    <>
      {/* parent outline for orientation while cutting */}
      {session.stage !== 'preview' && (
        <GeoJSON
          key={`split-parent-${session.id}`}
          data={{ type: 'Feature', geometry: session.geometry }}
          interactive={false}
          style={{ fillOpacity: 0, color: '#ffffff', weight: 2, dashArray: '6 4' }}
        />
      )}
      {/* candidate parcels, color-cycled so neighbours read apart */}
      {session.stage === 'preview' && session.parts && (
        <GeoJSON
          key={`split-parts-${session.parts.features.length}`}
          data={session.parts}
          interactive={false}
          style={(f) => ({
            fillColor: PART_COLORS[(f.properties?.idx ?? 0) % PART_COLORS.length],
            fillOpacity: 0.45,
            color: '#ffffff',
            weight: 1.5,
          })}
        />
      )}
      {session.lines.map((line, i) => (
        <Polyline key={`cut-${i}`} positions={line} pathOptions={{ color: CUT_COLOR, weight: 2.5 }} />
      ))}
      {session.open.length > 1 && (
        <Polyline positions={session.open} pathOptions={{ color: CUT_COLOR, weight: 2.5, dashArray: '5 4' }} />
      )}
      {session.open.map(([lat, lng], i) => (
        <CircleMarker
          key={`sv-${i}`}
          center={[lat, lng]}
          radius={4}
          pathOptions={{ color: CUT_COLOR, weight: 2, fillColor: '#fff', fillOpacity: 1 }}
        />
      ))}
    </>
  );
}
